import './patientInsuranceComponent.scss'
import React, {useCallback, useEffect, useRef, useState} from 'react';
import FormTitleWithNavigationBack
    from '../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack';
import * as Yup from "yup";
import _ from "lodash";
import {Field, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from 'formik';

import {Grid} from '@mui/material';
import ButtonComponent from '../../../../shared/components/core/button/ButtonComponent';
import {CommonService} from '../../../../shared/services';
import {IAPIResponseType} from '../../../../shared/models/api.model';
import FormikRadioButtonGroupComponent
    from '../../../../shared/components/core/formik-radio-button-group/formikRadioButtonGroupComponent';
import DrawerComponent from '../../../../shared/components/drawer/drawerComponent';
import InsuranceFormComponent from '../../../../shared/components/insurance-form/insuranceFormComponent';
import InsuranceDetailsComponent
    from '../../../../shared/components/insurance-details/insuranceDetailsComponent';
import EditIcon from '@mui/icons-material/Edit';
import StatusCardComponent from "../../../../shared/components/status-card/statusCardComponent";
import {ImageConfig} from "../../../../constants";
import LottieFileGenarationComponent
    from "../../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";

interface PatientInsuranceComponentProps {
    prev: Function;
    next: Function;
    patientID?: number;
    patientDetails?: any;
    cancel?: Function
}

interface PatientInsuranceDetailsFormSchema {
    isInsuranceAvailable: boolean,
}

const PatientInsuranceDetailsFormIntialValues: PatientInsuranceDetailsFormSchema = {
    isInsuranceAvailable: false,
}
const PatientInsuranceDetailsFormValidationSchema = Yup.object({
    isInsuranceAvailable: Yup.boolean().required("Insurance available is required"),
})

const PatientInsuranceComponent = (props: PatientInsuranceComponentProps) => {

        const {prev, patientID, next, cancel} = props;
        const [initialValues] = useState<PatientInsuranceDetailsFormSchema>(_.cloneDeep(PatientInsuranceDetailsFormIntialValues));
        const [insuranceInitialValues, setInsuranceInitialValues] = useState<any>("");
        const [patientInsuranceList, setPatientInsuranceList] = useState<any>([]);
        const [isPatientInsuranceListLoading, setIsPatientInsuranceListLoading] = useState<boolean>(false);
        const [isPatientInsuranceListLoaded, setIsPatientInsuranceListLoaded] = useState<boolean>(false);
        const [isPatientInsuranceListLoadingFailed, setIsPatientInsuranceListLoadingFailed] = useState<boolean>(false);
        const [openInsuranceDrawer, setOpenInsuranceDrawer] = useState<boolean>(false);
        const [insuranceMode, setInsuranceMode] = useState<string>("");

        const formikRef = useRef<FormikProps<FormikValues>>(null);

        const onUpdateComplete = useCallback((data: any) => {
            if (insuranceMode === 'add') {
                setPatientInsuranceList((oldState: any) => {
                    if (oldState?.length > 0) {
                        return [...oldState, data]
                    } else {
                        return [data]
                    }
                });
            }
            if (insuranceMode === 'edit') {
                const temporaryList = patientInsuranceList;
                const index = temporaryList.findIndex((insuranceDetails: any) => {
                    return insuranceDetails.id === data.id;
                });
                temporaryList[index] = data;
                setPatientInsuranceList(temporaryList);
            }
            setInsuranceInitialValues("");
            setOpenInsuranceDrawer(false);
        }, [insuranceMode, patientInsuranceList]);

        const getPatientInsuranceList = useCallback(() => {
            setIsPatientInsuranceListLoading(true);
            setIsPatientInsuranceListLoaded(false);
            setIsPatientInsuranceListLoadingFailed(false);
            const payload = {}
            CommonService._patient.getPatientInsuranceList(patientID, payload)
                .then((response: IAPIResponseType<any>) => {
                    if (response?.data) {
                        const sortedList=CommonService.sortPatientInsuranceList(response.data);
                        setPatientInsuranceList(sortedList);
                        if (sortedList?.length > 0) {
                            if (formikRef.current) {
                                formikRef.current.setFieldValue('isInsuranceAvailable', true);
                            }
                        }
                    }
                    setIsPatientInsuranceListLoading(false);
                    setIsPatientInsuranceListLoaded(true);
                    setIsPatientInsuranceListLoadingFailed(false);
                })
                .catch((error: any) => {
                    setIsPatientInsuranceListLoading(false);
                    setIsPatientInsuranceListLoaded(false);
                    setIsPatientInsuranceListLoadingFailed(true);
                });
        }, [patientID])

        useEffect(() => {
            if (patientID) {
                getPatientInsuranceList();
            }
        }, [patientID, getPatientInsuranceList])

        const handleInsuranceDrawer = useCallback((insuranceMode: any, insurance?: any) => {
            if (insuranceMode === 'edit') {
                setInsuranceInitialValues(insurance);
                setInsuranceMode('edit');
            }
            if (insuranceMode === 'add') {
                setInsuranceMode('add');
            }
            setOpenInsuranceDrawer(true);
        }, []);

        const onFormSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
            setSubmitting(true);
            const payload = {...values}
            setSubmitting(true);
            CommonService._patient.editPatientDetails(patientID, payload)
                .then((response: IAPIResponseType<any>) => {
                    setSubmitting(false);
                    next(response.data);
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                });
        }, [next, patientID]);

        const handleInsuranceDrawerClose = useCallback(() => {
            setInsuranceInitialValues("");
            setOpenInsuranceDrawer(false);
        }, [])


        return (
            <div className='patient-insurance-screen t-form-screen screen'>
                <FormTitleWithNavigationBack
                    title={'Insurance'}
                    tooltipData={"Added insurance will be verified. Only three (3) insurances per patient are accepted. Ensure that the uploaded images of insurance card are readable."}
                    handleClick={prev}
                />
                <div className='t-form t-form-back-navigation'>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={PatientInsuranceDetailsFormValidationSchema}
                        validateOnChange={true}
                        validateOnBlur={true}
                        validateOnMount={true}
                        enableReinitialize={true}
                        onSubmit={onFormSubmit}
                        innerRef={formikRef}
                    >
                        {({isSubmitting, values, isValid, touched, setFieldValue, validateForm, errors}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [validateForm, values]);
                            return (
                                <Form className="patient-insurance-details-add-holder">
                                    <div className='patient-insurance-isAvailable-wrapper'>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={3}>
                                                <div className='patient-insurance-isAvailable-header radio-button-title'>Do you have an
                                                    Insurance?
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={5}>
                                                <Field name={'isInsuranceAvailable'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikRadioButtonGroupComponent
                                                                direction={"row"}
                                                                formikField={field}
                                                                titleKey={'title'}
                                                                valueKey={'code'}
                                                                isValueBoolean={true}
                                                                onUpdate={(value: any) => {
                                                                    if (value) {
                                                                        getPatientInsuranceList();
                                                                    }
                                                                }}
                                                                options={CommonService._staticData.yesNoOptions}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={4}></Grid>
                                        </Grid>
                                    </div>
                                    <div className="t-form-controls-wrapper">
                                        {
                                            values['isInsuranceAvailable'] && <>
                                                <div className="horizontal-line"/>

                                                {
                                                    isPatientInsuranceListLoading &&
                                                    <div className="spinner-loading-wrapper">
                                                        <LottieFileGenarationComponent
                                                            loop={true}
                                                            autoplay={true}
                                                            animationData={ImageConfig.LoadingLottieFile}/>
                                                        <div>Patient Insurance is loading</div>
                                                    </div>
                                                }

                                                {
                                                    isPatientInsuranceListLoadingFailed &&
                                                    <StatusCardComponent title={"Patient insurance failed to load"}/>
                                                }

                                                {
                                                    isPatientInsuranceListLoaded && <>
                                                        {patientInsuranceList.length === 0 ? <>
                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <div className='patient-no-insurance-header'> No
                                                                            insurance
                                                                            have been added.
                                                                        </div>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <ButtonComponent
                                                                            type="button"
                                                                            color={"primary"}
                                                                            variant={"outlined"}
                                                                            fullWidth={true}
                                                                            handleClick={() => handleInsuranceDrawer("add")}
                                                                        >
                                                                            Add Insurance
                                                                        </ButtonComponent>
                                                                    </Grid>
                                                                </Grid>
                                                            </>
                                                            :
                                                            <div className="insurance-list-container">{
                                                                patientInsuranceList?.map((insurance: any, index: any) => {
                                                                    return (
                                                                        <div className='patient-insurance-card-component'>
                                                                            <div key={CommonService.getUUID()}>
                                                                                <div
                                                                                    className='insurance-card-header-wrapper'>
                                                                                    <div>
                                                                                        {`Insurance ${index + 1}`}
                                                                                    </div>

                                                                                    <ButtonComponent
                                                                                        type={'button'}
                                                                                        variant={'outlined'}
                                                                                        color={'primary'}
                                                                                        handleClick={() => handleInsuranceDrawer("edit", insurance)}
                                                                                        startIcon={
                                                                                            <EditIcon/>}>Edit Details</ButtonComponent>

                                                                                </div>
                                                                                <>
                                                                                    <InsuranceDetailsComponent
                                                                                        insuranceDetails={insurance}/>
                                                                                </>
                                                                            </div>
                                                                            {index < (patientInsuranceList.length - 1) &&
                                                                                <div className="horizontal-line"/>
                                                                            }
                                                                        </div>
                                                                    )
                                                                })
                                                            }
                                                                {patientInsuranceList.length < 3 &&
                                                                    <div className='add-insurance-button'
                                                                         onClick={() => handleInsuranceDrawer("add")}>
                                                                        + Add Insurance
                                                                    </div>}
                                                            </div>
                                                        }
                                                    </>
                                                }

                                            </>
                                        }
                                    </div>
                                    <div className='form-action-section'>
                                        <ButtonComponent
                                            type="button"
                                            color={"primary"}
                                            variant={"outlined"}
                                            fullWidth={true}
                                            handleClick={cancel}
                                        >
                                            Close
                                        </ButtonComponent>
                                        <ButtonComponent
                                            type="submit"
                                            color={"primary"}
                                            variant={"contained"}
                                            fullWidth={true}
                                            isLoading={isSubmitting}
                                            disabled={values.isInsuranceAvailable && !(values.isInsuranceAvailable && patientInsuranceList.length > 0)}
                                        >
                                            Submit
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }}
                    </Formik>

                </div>
                <DrawerComponent
                    open={openInsuranceDrawer}
                    closeOnBackDropClick={true}
                    closeOnEsc={true}
                    className={"drawer-wrapper"}
                    onClose={() => handleInsuranceDrawerClose()}
                >
                    <InsuranceFormComponent
                        formInitialValues={insuranceInitialValues}
                        patientID={patientID}
                        formTitle={""}
                        insuranceMode={insuranceMode}
                        onUpdateComplete={(data: any) => onUpdateComplete(data)}
                    />
                </DrawerComponent>
            </div>
        )
    }

;

export default PatientInsuranceComponent;
